/** GPU Vignette — fragment-shader port of `lib/filters/vignette.ts`.
 *
 *  Radial brightness falloff: distance-from-center (normalized to
 *  half-diagonal) drives a smoothstep between `midpoint` and 1.0;
 *  the result modulates a multiplicative factor `1 - k*t`. Positive
 *  amount darkens corners, negative brightens them. Identity
 *  (amount = 0) short-circuits CPU-side to a copy.
 *
 *  GLSL has a built-in `smoothstep(edge0, edge1, x)` matching the
 *  JS helper's `3t² - 2t³` formula exactly. Half-diagonal is
 *  computed CPU-side and passed as a uniform so the shader avoids
 *  a sqrt per pixel. */

import { applyIdentityCopy } from '../identity-copy';
import type { VignetteValue } from '../../filters/vignette';
import { applyShader } from '../runtime';

const VIGNETTE_FRAG = `#version 300 es
precision highp float;
in vec2 v_uv;
uniform sampler2D u_source;
uniform vec2 u_dims;       // (width, height)
uniform vec2 u_center;     // ((w-1)/2, (h-1)/2)
uniform float u_halfDiag;  // pre-computed sqrt(cx*cx + cy*cy)
uniform float u_midpoint;  // [0, 1]
uniform float u_k;         // amount / 100, in [-1, 1]
out vec4 outColor;

void main() {
  // Reconstruct the integer pixel coord from v_uv. v_uv = (i+0.5)/dims
  // for texel (i, j) with NEAREST sampling — subtracting 0.5 recovers
  // the integer pixel index, which is what the CPU's nested-for loop
  // iterates on. Matching this exactly keeps the radial distance
  // identical to JS byte-for-byte.
  vec2 px = v_uv * u_dims - 0.5;
  vec2 d = (px - u_center) / u_halfDiag;
  float r = length(d);
  float t = smoothstep(u_midpoint, 1.0, r);
  float factor = 1.0 - u_k * t;
  vec4 src = texture(u_source, v_uv);
  // Match JS: per-channel multiply, alpha unchanged.
  vec3 rgb = src.rgb * factor;
  // Match JS Uint8ClampedArray semantics: clamp + round-to-nearest
  // before the framebuffer write. The implicit RGBA8 conversion
  // would round anyway, but matching the JS path's explicit
  // clamp/round keeps boundary cases byte-identical.
  rgb = clamp(floor(rgb * 255.0 + 0.5) / 255.0, 0.0, 1.0);
  outColor = vec4(rgb, src.a);
}
`;

export function applyVignetteOnGpu(
  source: ImageData,
  target: HTMLCanvasElement,
  value: VignetteValue,
): boolean {
  const amount = Math.max(-100, Math.min(100, value.amount));
  if (amount === 0) {
    // Match JS short-circuit: amount=0 is a source copy. Done on the
    // CPU 2D path because invoking the shader for a guaranteed
    // no-op wastes a texture upload + drawImage.
    return applyIdentityCopy(source, target);
  }
  const midpoint = Math.max(0, Math.min(1, value.midpoint));
  const k = amount / 100;
  // Center coords use (w-1)/2 in JS — pass (w-1, h-1) so the shader
  // can match without needing the source's actual dims separately.
  const w = source.width;
  const h = source.height;
  const cx = (w - 1) / 2;
  const cy = (h - 1) / 2;
  const halfDiag = Math.sqrt(cx * cx + cy * cy);
  return applyShader({
    source,
    target,
    fragShaderSrc: VIGNETTE_FRAG,
    uniforms: {
      u_dims: { type: 'vec2', value: [w, h] },
      u_center: { type: 'vec2', value: [cx, cy] },
      u_halfDiag: { type: 'float', value: halfDiag },
      u_midpoint: { type: 'float', value: midpoint },
      u_k: { type: 'float', value: k },
    },
  });
}
